import { resolveMediaUrl } from './mediaUrl';

/**
 * Game Image Utility
 * Centralized logic for resolving game images from static assets
 * with fallbacks to backend uploads and placeholder images
 */

/**
 * Registry of available static game images in public/games/
 * Add new images here when you upload them to public/games/ folder
 * These images are committed to git and persist across deployments
 */
export const STATIC_GAME_IMAGES = [
  { path: '/games/cs2.png', name: 'CS2 / Counter-Strike 2', filename: 'cs2.png', category: 'competitive' },
  { path: '/games/chess.png', name: 'Chess', filename: 'chess.png', category: 'casual' },
  { path: '/games/wc3.png', name: 'Warcraft 3', filename: 'wc3.png', category: 'competitive' },
  { path: '/games/beerpong.png', name: 'Beer Pong', filename: 'beerpong.png', category: 'irl' },
  { path: '/games/flatout2.png', name: 'FlatOut 2', filename: 'flatout2.png', category: 'casual' },
  { path: '/games/geoguessr.png', name: 'GeoGuessr', filename: 'geoguessr.png', category: 'casual' },
  { path: '/games/golfwithyourfriends.png', name: 'Golf With Your Friends', filename: 'golfwithyourfriends.png', category: 'casual' },
  { path: '/games/putting.png', name: 'Putting / Mini Golf', filename: 'putting.png', category: 'irl' },
  { path: '/games/trackmania.png', name: 'Trackmania', filename: 'trackmania.png', category: 'competitive' },
  { path: '/games/assetto.png', name: 'Assetto Corsa', filename: 'assetto.png', category: 'competitive' },
  { path: '/games/blobby.png', name: 'Blobby Volley', filename: 'blobby.png', category: 'casual' },
  { path: '/games/fallback.png', name: 'Fallback', filename: 'fallback.png', category: 'casual' },
];

/**
 * Map game names to static image paths in public/games/
 * Images in this map are committed to git and persist across deployments
 * @deprecated Use STATIC_GAME_IMAGES registry and admin image picker instead
 */
export const getGameImagePath = (gameName) => {
  const gameImageMap = {
    'CS2': '/games/cs2.png',
    'Counter-Strike 2': '/games/cs2.png',
    'Chess': '/games/chess.png',
    'Warcraft 3': '/games/wc3.png',
    'WC3': '/games/wc3.png',
    'Beer Pong': '/games/beerpong.png',
    'Beerpong': '/games/beerpong.png',
    'Flatout 2': '/games/flatout2.png',
    'FlatOut 2': '/games/flatout2.png',
    'GeoGuessr': '/games/geoguessr.png',
    'Geoguessr': '/games/geoguessr.png',
    'Golf With Your Friends': '/games/golfwithyourfriends.png',
    'Golf with Your Friends': '/games/golfwithyourfriends.png',
    'Putting': '/games/putting.png',
    'Mini Golf': '/games/putting.png',
    'Trackmania': '/games/trackmania.png',
    'TrackMania': '/games/trackmania.png',
    'Assetto Corsa': '/games/assetto.png',
    'Assetto': '/games/assetto.png',
    'Blobby Volley': '/games/blobby.png',
    'Blobby': '/games/blobby.png',
  };
  
  return gameImageMap[gameName] || null;
};

const getPlaceholderImage = () => '/games/fallback.png';

/**
 * Check if a path is a static frontend image (from public/games/)
 */
export const isStaticImagePath = (path) => {
  return Boolean(path && path.startsWith('/games/'));
};

/**
 * Get game image with intelligent fallback chain:
 * 1. Check if background_image is a static path (/games/...)
 * 2. Try static image from public/games/ based on name (for backward compatibility)
 * 3. Try backend uploaded image (if exists)
 * 4. Try external URL (if provided)
 * 5. Fall back to the local theme placeholder
 * 
 * @param {Object} game - Game object with name and optional background_image
 * @returns {string} Image URL
 */
export const getGameImage = (game) => {
  // Priority 1: Check if background_image is already a static path
  if (game?.background_image && isStaticImagePath(game.background_image)) {
    return game.background_image;
  }
  
  // Priority 2: Try static image based on game name (backward compatibility)
  if (game?.name) {
    const staticImage = getGameImagePath(game.name);
    if (staticImage) return staticImage;
  }
  
  // Priority 3: Blob URL, external URL, or legacy backend upload path
  if (game?.background_image) {
    return resolveMediaUrl(game.background_image);
  }
  
  return getPlaceholderImage();
};

/**
 * Check if a game has a static image available
 * Useful for admin interfaces to show warnings
 */
export const hasStaticImage = (gameName) => {
  return getGameImagePath(gameName) !== null;
};

/**
 * Get category color for static image categories
 */
export const getCategoryColor = (category) => {
  switch (category) {
    case 'casual': return 'success';
    case 'competitive': return 'error';
    case 'irl': return 'warning';
    default: return 'default';
  }
};

